import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { logger } from "../../utils/logger";

interface Dish {
  id: string;
  name: string;
  is_available: boolean;
  [key: string]: unknown;
}

interface StatusCounts {
  unavailable?: number;
  limited?: number;
  available?: number;
}

const COLORS = {
  burgundy: "#8B2634",
  amber: "#B8860B",
  goldGreen: "#9CAF88",
  accent: "#C59D5F",
};

/**
 * Menu Availability Summary Card - Dashboard Widget
 *
 * Lightweight summary component showing menu availability stats
 * Click "Manage Availability" button to navigate to full management page
 */
function LowStockAlerts(): JSX.Element {
  const [allDishes, setAllDishes] = useState<Dish[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAllDishes = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("menu_items")
        .select("id, name, is_available")
        .order("created_at", { ascending: false });

      if (error) {
        logger.error("Error fetching menu items:", error);
        setAllDishes([]);
        return;
      }

      setAllDishes((data || []) as Dish[]);
    } catch (err) {
      logger.error("Error in fetchAllDishes:", err);
      setAllDishes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchAllDishes();

    const channel = supabase
      .channel("menu-availability-summary")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "menu_items" },
        () => {
          void fetchAllDishes();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchAllDishes]);

  const getDishStatus = (
    dish: Dish,
  ): "unavailable" | "limited" | "available" => {
    if (!dish.is_available) return "unavailable";
    return "available";
  };

  const statusCounts: StatusCounts = allDishes.reduce((acc, dish) => {
    const status = getDishStatus(dish);
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {} as StatusCounts);

  const totalAlerts =
    (statusCounts.unavailable || 0) + (statusCounts.limited || 0);
  const totalDishes = allDishes.length;
  const hasAlerts = totalAlerts > 0;

  if (loading) {
    return (
      <div
        className="rounded-2xl border backdrop-blur-xl p-6 animate-pulse"
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.03)",
          borderColor: "rgba(255, 255, 255, 0.1)",
        }}
        data-animate="fade-scale"
        data-animate-active="false"
      >
        <div className="flex items-center gap-3 mb-4">
          <div
            className="p-2 rounded-lg"
            style={{ backgroundColor: `${COLORS.amber}20` }}
          >
            <svg
              className="w-5 h-5"
              style={{ color: COLORS.amber }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <div>
            <h3
              className="text-lg font-semibold"
              style={{ color: "var(--text-heading)" }}
            >
              Menu Availability
            </h3>
            <p className="text-sm" style={{ color: "var(--text-body-muted)" }}>
              Loading menu availability...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl border backdrop-blur-xl p-6"
      style={{
        backgroundColor: "rgba(255, 255, 255, 0.03)",
        borderColor: "rgba(255, 255, 255, 0.1)",
      }}
      data-animate="fade-scale"
      data-animate-active="false"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          <div
            className="p-2 rounded-lg"
            style={{
              backgroundColor: hasAlerts
                ? `${COLORS.burgundy}20`
                : `${COLORS.goldGreen}20`,
            }}
          >
            <svg
              className="w-5 h-5"
              style={{
                color: hasAlerts ? COLORS.burgundy : COLORS.goldGreen,
              }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {hasAlerts ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              )}
            </svg>
          </div>
          <div className="flex-1">
            <h3
              className="text-lg font-semibold"
              style={{ color: "var(--text-heading)" }}
            >
              Menu Availability
            </h3>
            <p className="text-sm" style={{ color: "var(--text-body-muted)" }}>
              {hasAlerts
                ? `${totalAlerts} ${totalAlerts === 1 ? "item needs" : "items need"} attention`
                : `All ${totalDishes} menu items are available`}
            </p>
            {totalDishes > 0 && (
              <div
                className="flex items-center gap-4 mt-2 text-xs"
                style={{ color: "var(--text-body-muted)" }}
              >
                <span>
                  <span style={{ color: COLORS.goldGreen, fontWeight: "bold" }}>
                    {statusCounts.available || 0}
                  </span>{" "}
                  available
                </span>
                {statusCounts.unavailable && statusCounts.unavailable > 0 && (
                  <span>
                    <span
                      style={{ color: COLORS.burgundy, fontWeight: "bold" }}
                    >
                      {statusCounts.unavailable}
                    </span>{" "}
                    unavailable
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <Link
          to="/admin/menu-availability"
          className="px-4 py-2 rounded-lg font-medium text-sm text-black hover:opacity-90 transition-all duration-200 flex items-center gap-2 whitespace-nowrap ml-4"
          style={{ backgroundColor: COLORS.accent }}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
          Manage Availability
        </Link>
      </div>
    </div>
  );
}

export default LowStockAlerts;
